import React, { Component, Suspense, lazy } from 'react';
import Inherit from '../../core/inherit';
import withHooks from '../../core/withHooks';
import LoginController from './login.controller';
import ResetPassword from '../reset-password/reset-password';
import styles from './styles/login.module.scss';
import LoginErrorMessage from './elements/LoginErrorMessage';
import { SplitLayoutVariant } from '../common/layouts/splitLayout/splitLayout.constants';
import SplitLayout from '../common/layouts/splitLayout/SplitLayout';
import { Text, Input, Button, Checkbox } from '@clockout-dev/design-elements';
import AuthMarketingSideFallback from '../common/authMarketingSideFallback/AuthMarketingSideFallback';

const LoginSideContent = lazy(() => import('./elements/LoginSideContent'));

/**
 * @extends ClockoutController
 * @extends LoginController
 * @extends Component
 */
class Login extends Inherit(LoginController, Component) {
    state = {
        email: '',
        password: '',
        loading: false,
        error: false,
        errorMessage: '',
        showResetPassword: false
    };

    render() {
        const { email, password, error, errorMessage, loading, showResetPassword } = this.state;

        if (showResetPassword) {
            return <ResetPassword />;
        }

        return (
            <SplitLayout
                variant={SplitLayoutVariant.DEFAULT}
                side={
                    <Suspense fallback={<AuthMarketingSideFallback variant="login" />}>
                        <LoginSideContent />
                    </Suspense>
                }
                contentClassName={styles.loginContentContainer}
                sideClassName={styles.loginSideColumn}>
                <div className={styles.loginContent}>
                    <div className={styles.loginContentWrapper}>
                        <div className={styles.logoContainer}>
                            <img src="/logo-dark.png" alt="Clockout Logo" className={styles.logo} />
                        </div>
                        <div className={styles.titleContainer}>
                            <Text as="h1" size="display-l" className={styles.title}>Welcome back</Text>
                            <Text as="p" size="l" className={styles.subtitle}>Enter your details to access your dashboard.</Text>
                        </div>
                        <div className={styles.loginCard}>
                            <LoginErrorMessage error={error} errorMessage={errorMessage} />
                            
                            <div className={styles.loginForm}>
                                <Input
                                    label="Email"
                                    id="email"
                                    type="email"
                                    labelSize="xl"
                                    value={email || ''}
                                    onChange={(e) => this.setEmail(e.target.value)}
                                    onPressEnter={() => !loading && this.authenticateUser()}
                                    placeholder="name@company.com"
                                    status={error ? 'error' : ''}
                                    className={`${styles.input} ${error ? styles.inputError : ''}`}
                                    autoComplete="email"
                                    classNames={{label: styles.inputLabel}}
                                />

                                <Input.Password
                                    label="Password"
                                    id="password"
                                    value={password || ''}
                                    onChange={(e) => this.setPassword(e.target.value)}
                                    onPressEnter={() => !loading && this.authenticateUser()}
                                    placeholder="*******"
                                    labelSize="l"
                                    status={error ? 'error' : ''}
                                    className={`${styles.input} ${error ? styles.inputError : ''}`}
                                    autoComplete="current-password"
                                    classNames={{label: styles.inputLabel}}
                                />

                                <Checkbox size="large" text="Remember this device for 30 days" classNames={{text: styles.rememberDeviceCheckbox}} />

                                <Button
                                    type="button"
                                    size="large"
                                    disabled={loading}
                                    onClick={() => this.authenticateUser()}
                                    className={styles.signInButton}>
                                        Sign in
                                </Button>
                            </div>
                        </div>
                    </div>
                    <div className={styles.footer}>
                        <Text size="m" as="a" href="https://www.joinclockout.com/privacy-policy" target="_blank" className={styles.footerLink}>Privacy Policy</Text>
                        <Text size="m" as="a" href="https://www.joinclockout.com/terms-of-service" target="_blank" className={styles.footerLink}>Terms</Text>
                        {/* TODO: wire this up to the system status page */}
                        <Text size="m" className={styles.footerLink}>System Status.</Text>
                    </div>
                </div>
            </SplitLayout>
        )
    }
}

export default withHooks(Login);
